import React, { useCallback, useState } from 'react';

import styledC from 'styled-components';

import { useTheme } from '@adsk/alloy-react-styles';

export enum DividerPosition {
  left = 'left',
  right = 'right',
}

const StyledDividerContainer = styledC.div<{
  $vertical?: boolean;
  $position: DividerPosition;
  $width: number;
  $zIndex?: number | string;
}>`
  z-index: ${(props) => props.$zIndex};
  height: ${(props) => (props.$vertical ? '100%' : '')};
  width: ${(props) => (props.$vertical ? `${props.$width}px` : '')};
  position: absolute;
  top: 0;
  left: ${(props) =>
    props.$position === DividerPosition.left
      ? `-${props.$width / 2}px`
      : 'unset'};
  right: ${(props) =>
    props.$position === DividerPosition.right
      ? `-${props.$width / 2}px`
      : 'unset'};
`;

const StyledDividerHighlight = styledC.div<{
  $vertical?: boolean;
}>`
  transition: background-color 0.1s ease-in-out;
  margin: auto;
  height: ${(props) => (props.$vertical ? '100%' : '')};
`;

const StyledDivider = styledC.div<{
  $vertical?: boolean;
  $dragging?: boolean;
  $affordanceWidth: number;
  $position?: DividerPosition;
  $minReached?: boolean;
  $maxReached?: boolean;
  $hoverWidth: number;
  $activeWidth: number;
  $minMaxIndicatorWidth: number;
  $edgeColor?: string;
  $centerColor?: string;
}>`
  height: ${(props) => (props.$vertical ? '100%' : '')};
  width: ${(props) => (props.$vertical ? `${props.$affordanceWidth}px` : '')};
  cursor: ${(props) => (props.$vertical ? 'ew-resize' : '')};

  &:hover {
    ${StyledDividerHighlight} {
      background: ${(props) =>
        `linear-gradient(to left, ${props.$edgeColor}, ${props.$centerColor} 50%, ${props.$edgeColor})`};
    }
  }

  ${StyledDividerHighlight} {
    background: ${(props) => {
      let firstColor = 'transparent';
      const lastColor = 'transparent';
      let direction = 'to left';

      if (props.$minReached || props.$maxReached) {
        firstColor = 'transparent 50%';

        if (props.$minReached) {
          direction =
            props.$position === DividerPosition.left ? 'to right' : 'to left';
        }

        if (props.$maxReached) {
          direction =
            props.$position === DividerPosition.left ? 'to left' : 'to right';
        }
      }

      return props.$dragging
        ? `linear-gradient(${direction}, ${firstColor}, ${props.$centerColor} 50%, ${lastColor})`
        : 'none';
    }};

    width: ${(props) => {
      let width = props.$dragging ? props.$activeWidth : props.$hoverWidth;

      if (props.$dragging && (props.$minReached || props.$maxReached)) {
        width = props.$minMaxIndicatorWidth;
      }

      // ensure this never gets wider than resize affordance
      width = Math.min(width, props.$affordanceWidth);

      if (props.$vertical) {
        return `${width}px`;
      } else {
        return '';
      }
    }};

    ${(props) =>
      props.$maxReached || props.$minReached
        ? 'transition: width 0.5s ease-in;'
        : ''}
  }
`;

const StyledMouseCaptureLayer = styledC.div<{
  $vertical?: boolean;
  $dragging?: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 10000;
  user-select: none;

  pointer-events: ${(props) => (props.$dragging ? 'all' : 'none')};

  cursor: ${(props) => {
    if (props.$dragging) {
      if (props.$vertical) {
        return 'ew-resize';
      } else {
        return 'ns-resize';
      }
    }

    return '';
  }};
`;

interface Props {
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  vertical?: boolean;
  position?: DividerPosition;
  affordanceWidth: number;
  hoverWidth?: number;
  activeWidth?: number;
  minMaxIndicatorWidth?: number;
  minReached?: boolean;
  maxReached?: boolean;
}

export const ResizeDivider: React.FC<Props> = ({
  onMouseDown,
  onMouseUp,
  onMouseMove,
  vertical = false,
  position = DividerPosition.left,
  affordanceWidth = 16,
  hoverWidth = 2,
  activeWidth = 6,
  minMaxIndicatorWidth = 14,
  minReached = false,
  maxReached = false,
}: Props) => {
  const dynamicTheme = useTheme();
  const resizeTokens = dynamicTheme.tokens?.component?.panel?.resize;
  const [dragging, setDragging] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setDragging(true);
      onMouseDown(e);
      e.preventDefault();
      e.stopPropagation();
    },
    [onMouseDown],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      setDragging(false);
      onMouseUp(e);
      e.preventDefault();
      e.stopPropagation();
    },
    [onMouseUp],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      onMouseMove(e);
      e.preventDefault();
      e.stopPropagation();
    },
    [onMouseMove],
  );

  return (
    <StyledDividerContainer
      $vertical={vertical}
      $position={position}
      $width={affordanceWidth}
      $zIndex={resizeTokens?.['z-index']}
    >
      <StyledDivider
        data-testid="resizeDivider"
        onMouseDown={handleMouseDown}
        $vertical={vertical}
        $dragging={dragging}
        $affordanceWidth={affordanceWidth}
        $hoverWidth={hoverWidth}
        $activeWidth={activeWidth}
        $minMaxIndicatorWidth={minMaxIndicatorWidth}
        $position={position}
        $minReached={minReached}
        $maxReached={maxReached}
        $edgeColor={resizeTokens?.['gradient-edge-color']}
        $centerColor={resizeTokens?.['gradient-center-color']}
      >
        <StyledDividerHighlight $vertical={vertical} />
      </StyledDivider>
      <StyledMouseCaptureLayer
        $vertical={vertical}
        $dragging={dragging}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      />
    </StyledDividerContainer>
  );
};
